import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Trash2, Crown, Banknote, Wallet, TrendingDown } from "lucide-react";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";

const TYPE_LABELS = {
  vale: { label: "Vale", color: "#F59E0B", bg: "#FEF3C7", sign: -1 },
  falta: { label: "Falta (descontar)", color: "#DC2626", bg: "#FEE2E2", sign: -1 },
  salario: { label: "Salário pago", color: "#166534", bg: "#D1FAE5", sign: -1 },
  comissao: { label: "Comissão paga", color: "#CBA871", bg: "#FEF3C7", sign: -1 },
  bonus: { label: "Bônus", color: "#7C3AED", bg: "#EDE9FE", sign: 1 },
  ajuste: { label: "Ajuste", color: "#737373", bg: "#F3F4F6", sign: -1 },
};

const PM_LABELS = {
  dinheiro: "Dinheiro",
  pix: "PIX",
  transferencia: "Transferência",
};

export default function EmployeePayments() {
  const { apiCall, user } = useAuth();
  const isMaster = user?.role === "master";
  const [payments, setPayments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [filterEmployee, setFilterEmployee] = useState("all");
  const [form, setForm] = useState({
    employee_id: "",
    type: "vale",
    amount: "",
    payment_method: "dinheiro",
    description: "",
    date: new Date().toISOString().slice(0, 10),
  });

  const load = async () => {
    setLoading(true);
    try {
      const [pRes, eRes] = await Promise.all([
        apiCall("get", "/employee-payments"),
        apiCall("get", "/employees"),
      ]);
      setPayments(pRes.data);
      setEmployees(eRes.data);
    } catch {
      toast.error("Erro ao carregar pagamentos");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isMaster) {
      toast.error("Apenas usuários master podem registrar pagamentos a funcionários");
      return;
    }
    try {
      await apiCall("post", "/employee-payments", {
        ...form,
        amount: parseFloat(form.amount),
        date: form.date ? new Date(form.date).toISOString() : null,
      });
      toast.success("Pagamento registrado");
      setOpen(false);
      setForm({
        employee_id: "",
        type: "vale",
        amount: "",
        payment_method: "dinheiro",
        description: "",
        date: new Date().toISOString().slice(0, 10),
      });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao salvar");
    }
  };

  const handleDelete = async (id) => {
    if (!isMaster) return toast.error("Apenas master");
    if (!confirm("Excluir este pagamento? A despesa relacionada também será removida.")) return;
    try {
      await apiCall("delete", `/employee-payments/${id}`);
      toast.success("Pagamento excluído");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao excluir");
    }
  };

  const formatCurrency = (v) =>
    new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(v || 0);

  const formatDate = (date) => new Date(date).toLocaleDateString("pt-BR");

  const filtered = filterEmployee === "all" ? payments : payments.filter((p) => p.employee_id === filterEmployee);

  // Totals
  const totals = filtered.reduce(
    (acc, p) => {
      acc.total += p.amount;
      if (!acc.byType[p.type]) acc.byType[p.type] = 0;
      acc.byType[p.type] += p.amount;
      return acc;
    },
    { total: 0, byType: {} }
  );

  return (
    <div className="space-y-6 lg:space-y-8" data-testid="employee-payments-page">
      <PageHeader
        overline="Equipe"
        title="Vales & Pagamentos"
        subtitle="Registre vales, salários, comissões e bônus pagos aos funcionários"
      >
        <Button onClick={() => setOpen(true)} disabled={!isMaster || employees.length === 0} data-testid="add-payment-button">
          <Plus className="h-4 w-4 mr-2" strokeWidth={1.5} />
          Novo Pagamento
        </Button>
      </PageHeader>

      {!isMaster && (
        <Card className="border border-accent/40 bg-accent/5 p-4 shadow-soft rounded-md">
          <div className="flex items-center gap-3 text-sm">
            <Crown className="h-4 w-4 flex-shrink-0" strokeWidth={2} style={{ color: "#CBA871" }} />
            <span className="text-muted-foreground">
              Apenas usuários <span className="text-foreground font-medium">master</span> podem registrar ou excluir pagamentos.
            </span>
          </div>
        </Card>
      )}

      {/* Summary */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="border border-border bg-card p-5 shadow-soft rounded-md" data-testid="payments-total">
          <div className="flex items-start justify-between mb-2">
            <div className="text-xs uppercase tracking-widest text-muted-foreground font-heading">Total Pago</div>
            <TrendingDown className="h-4 w-4 text-destructive" strokeWidth={1.5} />
          </div>
          <div className="text-2xl font-light tracking-tight font-heading text-destructive">
            {formatCurrency(totals.total)}
          </div>
        </Card>
        <Card className="border border-border bg-card p-5 shadow-soft rounded-md" data-testid="payments-vales">
          <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2 font-heading">Vales</div>
          <div className="text-2xl font-light tracking-tight font-heading" style={{ color: "#F59E0B" }}>
            {formatCurrency(totals.byType.vale || 0)}
          </div>
        </Card>
        <Card className="border border-border bg-card p-5 shadow-soft rounded-md" data-testid="payments-faltas">
          <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2 font-heading">Faltas (descontar)</div>
          <div className="text-2xl font-light tracking-tight font-heading" style={{ color: "#DC2626" }}>
            {formatCurrency(totals.byType.falta || 0)}
          </div>
        </Card>
        <Card className="border border-border bg-card p-5 shadow-soft rounded-md" data-testid="payments-salarios">
          <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2 font-heading">Salários</div>
          <div className="text-2xl font-light tracking-tight font-heading" style={{ color: "#166534" }}>
            {formatCurrency(totals.byType.salario || 0)}
          </div>
        </Card>
      </div>

      {/* Filter */}
      <div className="flex items-center gap-3 flex-wrap">
        <Label className="text-xs uppercase tracking-widest">Filtrar por funcionário</Label>
        <Select value={filterEmployee} onValueChange={setFilterEmployee}>
          <SelectTrigger className="w-64" data-testid="filter-employee-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all" data-testid="filter-employee-all">Todos os funcionários</SelectItem>
            {employees.map((e) => (
              <SelectItem key={e.employee_id} value={e.employee_id} data-testid={`filter-employee-${e.employee_id}`}>
                {e.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <Card className="border border-border bg-card shadow-soft rounded-md overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-secondary/30 hover:bg-secondary/30">
              <TableHead className="text-xs uppercase tracking-widest font-medium">Data</TableHead>
              <TableHead className="text-xs uppercase tracking-widest font-medium">Funcionário</TableHead>
              <TableHead className="text-xs uppercase tracking-widest font-medium">Tipo</TableHead>
              <TableHead className="text-xs uppercase tracking-widest font-medium">Forma</TableHead>
              <TableHead className="text-xs uppercase tracking-widest font-medium">Descrição</TableHead>
              <TableHead className="text-xs uppercase tracking-widest font-medium">Valor</TableHead>
              <TableHead className="text-xs uppercase tracking-widest font-medium">Registrado por</TableHead>
              <TableHead className="text-right text-xs uppercase tracking-widest font-medium">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={8} className="text-center py-8 text-muted-foreground">Carregando...</TableCell></TableRow>
            ) : filtered.length === 0 ? (
              <TableRow><TableCell colSpan={8} className="text-center py-12 text-muted-foreground">Nenhum pagamento registrado.</TableCell></TableRow>
            ) : (
              filtered.map((p) => (
                <TableRow key={p.payment_id} data-testid={`payment-row-${p.payment_id}`}>
                  <TableCell className="text-sm text-muted-foreground">{formatDate(p.date)}</TableCell>
                  <TableCell className="font-medium">{p.employee_name}</TableCell>
                  <TableCell>
                    <span
                      className="text-xs uppercase tracking-widest font-medium px-2 py-1 rounded"
                      style={{ background: TYPE_LABELS[p.type]?.bg, color: TYPE_LABELS[p.type]?.color }}
                    >
                      {TYPE_LABELS[p.type]?.label || p.type}
                    </span>
                  </TableCell>
                  <TableCell className="text-sm">{PM_LABELS[p.payment_method] || p.payment_method}</TableCell>
                  <TableCell className="text-sm text-muted-foreground max-w-xs truncate">{p.description || "—"}</TableCell>
                  <TableCell className="font-medium text-destructive">{formatCurrency(p.amount)}</TableCell>
                  <TableCell className="text-xs text-muted-foreground flex items-center gap-1">
                    <Crown className="h-3 w-3" strokeWidth={2} style={{ color: "#CBA871" }} />
                    {p.created_by_name}
                  </TableCell>
                  <TableCell className="text-right">
                    {isMaster && (
                      <Button variant="ghost" size="icon" onClick={() => handleDelete(p.payment_id)} data-testid={`delete-payment-${p.payment_id}`}>
                        <Trash2 className="h-4 w-4 text-destructive" strokeWidth={1.5} />
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-light tracking-tight text-2xl flex items-center gap-2">
              <Crown className="h-5 w-5" strokeWidth={2} style={{ color: "#CBA871" }} />
              Novo Pagamento ao Funcionário
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            <div>
              <Label className="text-xs uppercase tracking-widest">Funcionário</Label>
              <Select value={form.employee_id} onValueChange={(v) => setForm({ ...form, employee_id: v })} required>
                <SelectTrigger data-testid="payment-employee-select"><SelectValue placeholder="Selecione" /></SelectTrigger>
                <SelectContent>
                  {employees.map((e) => (
                    <SelectItem key={e.employee_id} value={e.employee_id} data-testid={`payment-employee-option-${e.employee_id}`}>
                      {e.name} {e.role ? `(${e.role})` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest">Tipo</Label>
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                <SelectTrigger data-testid="payment-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(TYPE_LABELS).map(([k, v]) => (
                    <SelectItem key={k} value={k} data-testid={`payment-type-${k}`}>{v.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs uppercase tracking-widest">Valor (R$)</Label>
                <Input type="number" step="0.01" min="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} required data-testid="payment-amount-input" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest">Data</Label>
                <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} required data-testid="payment-date-input" />
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest">Forma de Pagamento</Label>
              <Select value={form.payment_method} onValueChange={(v) => setForm({ ...form, payment_method: v })}>
                <SelectTrigger data-testid="payment-method-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(PM_LABELS).map(([k, v]) => (
                    <SelectItem key={k} value={k} data-testid={`payment-method-${k}`}>{v}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest">Descrição (opcional)</Label>
              <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Ex: Vale adiantado mês 02" data-testid="payment-description-input" />
            </div>
            <div className="p-3 bg-secondary/50 rounded-md text-xs text-muted-foreground">
              <Wallet className="inline h-3 w-3 mr-1" strokeWidth={1.5} />
              Uma despesa será criada automaticamente no Financeiro.
            </div>
            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
              <Button type="submit" data-testid="payment-save-button">
                <Banknote className="h-4 w-4 mr-2" strokeWidth={1.5} />
                Registrar Pagamento
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
